import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, ClipboardCheck, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Periodic-review modal — guided 5-prompt flow that produces a single
// journal entry tagged "weekly-review" or "monthly-review". The
// /journal/review-status endpoint reads the most recent entry of each
// tag to determine "due" state.

const WEEKLY_PROMPTS = [
  { key: 'best',    label: 'Best decision this week?' },
  { key: 'worst',   label: 'Worst decision this week?' },
  { key: 'pattern', label: 'Pattern noticed (mood, sector, time of day)?' },
  { key: 'next',    label: 'Next week\'s focus?' },
  { key: 'mood',    label: 'Mood / overall conviction this week?' },
];

const MONTHLY_PROMPTS = [
  { key: 'wins',         label: 'Wins this month — what worked?' },
  { key: 'mistakes',     label: 'Mistakes — what went sideways?' },
  { key: 'thesis_check', label: 'Are your active theses still intact? Any to retire?' },
  { key: 'cash_flow',    label: 'Dividend / cash flow vs your goal — on track?' },
  { key: 'next_month',   label: 'Next month — one specific change you\'ll make?' },
];

export default function ReviewModal({ kind, onClose, onSaved }) {
  const prompts = kind === 'monthly' ? MONTHLY_PROMPTS : WEEKLY_PROMPTS;
  const [answers, setAnswers] = useState(() =>
    Object.fromEntries(prompts.map(p => [p.key, '']))
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSnooze = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/journal/review-snooze`,
        { kind },
        { withCredentials: true },
      );
      const label = kind === 'monthly' ? 'next month' : 'next week';
      toast.success(`Snoozed — we'll ask ${label}`, {
        description: 'The review prompt and the amber Journal-button glow are off until then.',
      });
      onSaved && onSaved();
    } catch (err) {
      toast.error('Snooze failed', { description: err?.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const filled = prompts.filter(p => (answers[p.key] || '').trim().length > 0);
    if (filled.length === 0) {
      toast.error('Answer at least one prompt before saving.');
      return;
    }
    setSaving(true);
    try {
      // Compose the answers into a single Markdown body so entries
      // render cleanly in the timeline. Empty prompts are skipped so
      // the user isn't pressured to fill all five.
      const body = prompts
        .filter(p => (answers[p.key] || '').trim())
        .map(p => `**${p.label}**\n${answers[p.key].trim()}`)
        .join('\n\n');
      const tagSlug = kind === 'monthly' ? 'monthly-review' : 'weekly-review';
      const titleDate = new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
      await axios.post(
        `${API}/journal/entries`,
        {
          title: `${kind === 'monthly' ? 'Monthly' : 'Weekly'} Review — ${titleDate}`,
          body,
          entry_type: 'review',
          tags: [tagSlug],
        },
        { withCredentials: true },
      );
      toast.success(`${kind === 'monthly' ? 'Monthly' : 'Weekly'} review saved 📓`, {
        description: 'Pattern detection compounds — keep showing up.',
      });
      onSaved && onSaved();
    } catch (err) {
      toast.error('Save failed', { description: err?.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3" data-testid={`review-modal-${kind}`}>
      <div className="bg-background rounded-xl border-2 border-violet-400/60 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5 space-y-4 pb-24 sm:pb-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-violet-700 dark:text-violet-300 inline-flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5" />
            {kind === 'monthly' ? 'Monthly Review' : 'Weekly Review'}
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted" data-testid="review-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-xs text-muted-foreground">
          Answer what speaks to you — empty prompts are skipped. The whole point is honest reflection,
          not box-checking.
        </p>

        <div className="space-y-3">
          {prompts.map(p => (
            <div key={p.key}>
              <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">{p.label}</label>
              <Textarea
                value={answers[p.key]}
                onChange={(e) => setAnswers({ ...answers, [p.key]: e.target.value })}
                rows={2}
                data-testid={`review-prompt-${p.key}`}
                className="mt-1 text-sm"
              />
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 justify-end pt-2 border-t">
          {/* Round 71o (Feb 2026): "Ask me next week/month" snooze.
              Hides the review button + clears the amber Journal-
              button glow for 7 / 30 days. */}
          <Button
            type="button"
            variant="ghost"
            onClick={handleSnooze}
            disabled={saving}
            className="mr-auto text-muted-foreground hover:text-foreground"
            data-testid="review-modal-snooze"
            title="Hide the review prompt for the next period"
          >
            <Clock className="w-4 h-4 mr-1" />
            {kind === 'monthly' ? 'Ask me next month' : 'Ask me next week'}
          </Button>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="review-modal-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="review-modal-save"
          >
            {saving ? 'Saving…' : 'Save review'}
          </Button>
        </div>
      </div>
    </div>
  );
}
